import * as React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import Slide from "@mui/material/Slide";
import { DialogContent, useMediaQuery } from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useMemo } from "react";
import { useAxios } from "../../hooks";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { getDateTime } from "../../utilities/getDateTime";
import { useState } from "react";
import { useParams } from "react-router-dom";
import NoRowOverlay from "../common/NoRowOverlay";
import { formatDuration } from "../../utilities/formatDuration";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function AddPackagesDialog({ open, handleClose }) {
  const isSmallToMediumScreen = useMediaQuery((theme) =>
    theme.breakpoints.down("md")
  );
  const { id } = useParams();

  const api = useAxios({ autoSnackbar: true });
  const queryClient = useQueryClient();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const [selectedPackages, setSelectedPackages] = useState([]);

  const [isAddButtonDisabled, setIsAddButtonDisabled] = useState(true);

  const { data: res, isLoading } = useQuery(
    `company-package-list_active_page=${page}_rowsPerPage=${pageSize}`,
    () => {
      return api.get(
        `/api/company/i/package/${id}/p?page=${page}&rowsPerPage=${pageSize}`
      );
    }
  );

  const { mutate } = useMutation(
    () => {
      return api.post(`/api/company/i/add/${id}`, {
        list: selectedPackages,
      });
    },
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries(
          `company-package-list_active_page=${page}_rowsPerPage=${pageSize}`
        );
        queryClient.invalidateQueries(`package-list_active_${id}`);
      },
    }
  );

  const handleSelectionModelChange = (selectionModel) => {
    setSelectedPackages(selectionModel);
    setIsAddButtonDisabled(selectionModel.length === 0);
  };

  const handleAddClick = () => {
    if (selectedPackages.length > 0) {
      mutate();
      handleClose();
    }
  };

  const columns = useMemo(
    () => [
      { field: "id", headerName: "ID", flex: 0.2 },
      { field: "name", headerName: "Package Name", minWidth: 200, flex: 2 },
      { field: "price", headerName: "Price", minWidth: 100, flex: 1 },
      { field: "tax", headerName: "Tax", minWidth: 100, flex: 1 },
      { field: "type", headerName: "Type", minWidth: 100, flex: 1 },
      {
        field: "duration",
        headerName: "Duration",
        minWidth: 100,
        flex: 1,
        valueFormatter: (params) => formatDuration(params.value),
      },
      {
        field: "created_at",
        headerName: "Created At",
        minWidth: 100,
        flex: 1,
        valueFormatter: (params) => getDateTime(params.value),
      },
    ],
    []
  );

  return (
    <Dialog
      open={open}
      fullScreen={isSmallToMediumScreen}
      onClose={handleClose}
      TransitionComponent={Transition}
      PaperProps={{
        sx: { height: { md: 400 }, minWidth: { lg: 600, md: 600 } },
      }}
    >
      <AppBar sx={{ position: "relative" }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
            Packages
          </Typography>
          <Button
            autoFocus
            color="inherit"
            onClick={handleAddClick}
            disabled={isAddButtonDisabled}
          >
            Add
          </Button>
        </Toolbar>
      </AppBar>
      <DialogContent>
        <DataGrid
          columns={columns}
          rowCount={res?.data?.total ?? 0}
          rows={res?.data?.rows ?? []}
          loading={isLoading}
          pagination
          paginationMode="server"
          checkboxSelection
          onSelectionModelChange={handleSelectionModelChange}
          page={page}
          pageSize={pageSize}
          onPageChange={(v) => setPage(v)}
          onPageSizeChange={(v) => setPageSize(v)}
          components={{
            Toolbar: GridToolbar,
            NoRowsOverlay: NoRowOverlay,
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
